import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { getTracesAroundLogItem } from 'api/snapshotAlarmLogApi';
import { useAppSelector } from 'app/hooks';
import InfoPopup from 'components/InfoPopup';
import { SnapshotAlarmLogReturnWithStop } from 'features/alarmLog/types';
import {
  combineMessageFields,
  findClosestTraceIndex,
  getLogIndexForManySnapshots,
  getLogIndexForOneSnapshot,
  getLogTime,
  removeDuplicateSnapshots,
} from 'features/alarmLog/utils/logManipulation';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TraceHistory } from 'types';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { useAllSnapshotsWithoutCrosspol } from '../snapshots/api/getAllSnapshotsWithoutCrosspol';

interface Props {
  item: SnapshotAlarmLogReturnWithStop | null;
  handleClose: () => void;
}

const AlarmPopup = ({ item, handleClose }: Props) => {
  const { dateFormat, timeFormat, timezone } = useUserDateTime();
  const navigate = useNavigate();
  const { data: snapshots } = useAllSnapshotsWithoutCrosspol({});
  const inImperial = useAppSelector(
    (state) => state.auth.user?.us_customary_units
  );
  const [selectedLogItem, setSelectedLogItem] = useState('');
  const [noHistoryPopupOpen, setNoHistoryPopupOpen] = useState(false);
  const handleSnapshotSelect = (event: SelectChangeEvent) => {
    setSelectedLogItem(event.target.value);
  };

  const handleGoToTraceHistory = async (
    alarmLog: SnapshotAlarmLogReturnWithStop,
    alarmStop: boolean
  ) => {
    let traces: TraceHistory[] = [];
    const logTime = getLogTime(alarmLog, alarmStop);
    if (
      removeDuplicateSnapshots(alarmLog.snapshot_alarm_log_items).length === 1
    ) {
      const logIndex = getLogIndexForOneSnapshot(alarmLog, alarmStop);
      traces = await getTracesAroundLogItem(
        alarmLog.snapshot_alarm_log_items[logIndex].id,
        50,
        50
      );
    } else {
      const logIndex = getLogIndexForManySnapshots(
        alarmLog,
        alarmStop,
        selectedLogItem
      );
      traces =
        logIndex === undefined
          ? []
          : await getTracesAroundLogItem(logIndex, 50, 50);
    }
    if (traces.length === 0) {
      setNoHistoryPopupOpen(true);
    } else {
      const fromDate = traces[0].time;
      const toDate = traces[traces.length - 1].time;
      if (fromDate > logTime || toDate < logTime) {
        setNoHistoryPopupOpen(true);
        return;
      }
      const centerIndex = findClosestTraceIndex(traces, logTime);
      navigate(
        `/trace-history?analyzer=${traces[0].analyzer_id}&snapshot=${traces[0].snapshot_id}&start=${fromDate}&end=${toDate}&trace=${centerIndex}`
      );
    }
  };

  if (item === null) return null;

  return (
    <Dialog
      transitionDuration={0}
      fullWidth
      maxWidth={'sm'}
      open={true}
      onClose={handleClose}
    >
      <DialogTitle>Alarm Details</DialogTitle>
      <DialogContent style={{ paddingBottom: '.7rem' }}>
        <TextField
          variant='outlined'
          fullWidth
          multiline
          disabled
          value={combineMessageFields(
            item,
            inImperial,
            `${dateFormat} ${timeFormat}`,
            timezone
          )}
          sx={{
            '& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled': {
              color: (theme) => theme.palette.text.primary,
              '-webkit-text-fill-color': (theme) => theme.palette.text.primary,
            },
          }}
        ></TextField>
        <Grid
          style={{ marginTop: '0px' }}
          container
          alignItems='center'
          justifyContent='flex-end'
          spacing={1}
        >
          {removeDuplicateSnapshots(item.snapshot_alarm_log_items).length >
            1 && (
            <Grid item>
              <FormControl sx={{ minWidth: 250 }}>
                <InputLabel id='select-snapshot-label'>Snapshot</InputLabel>
                <Select
                  labelId='select-snapshot-label'
                  value={selectedLogItem}
                  onChange={handleSnapshotSelect}
                  label='Snapshot'
                >
                  {removeDuplicateSnapshots(item.snapshot_alarm_log_items).map(
                    (item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {
                          snapshots?.find(
                            (snapshot) => snapshot.id === item.snapshot_id
                          )?.name
                        }
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item>
            <Button
              color='primary'
              onClick={() => {
                handleGoToTraceHistory(item, false);
              }}
              disabled={
                removeDuplicateSnapshots(item.snapshot_alarm_log_items).length >
                  1 &&
                (!selectedLogItem ||
                  !item.snapshot_alarm_log_items
                    .map((item) => item.id)
                    .includes(Number(selectedLogItem)))
              }
            >
              Go To {item.stopTime ? 'Start' : 'Trace'} History
            </Button>
          </Grid>
          {item.stopTime && (
            <Grid item>
              <Button
                color='primary'
                onClick={() => {
                  handleGoToTraceHistory(item, true);
                }}
                disabled={
                  removeDuplicateSnapshots(item.snapshot_alarm_log_items)
                    .length > 1 &&
                  (!selectedLogItem ||
                    !item.snapshot_alarm_log_items
                      .map((item) => item.id)
                      .includes(Number(selectedLogItem)))
                }
              >
                Go To Stop History
              </Button>
            </Grid>
          )}
          <Grid item>
            <Button onClick={handleClose}>Close</Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ paddingTop: 0 }}></DialogActions>
      <InfoPopup
        openCondition={noHistoryPopupOpen}
        title='No History Found'
        message='There is no recorded trace history for this alarm and the selected snapshot.'
        error
      />
    </Dialog>
  );
};

export default AlarmPopup;
